import { AlertTriangle, Check } from "lucide-react";
import type { TokenBudgetEstimateDto } from "@/api/reportsV32";
import { cn } from "@/design-system/lib/utils";

// LLM & Reports v3.2 §6.6 / §8.1 step 6 — surfaces the heuristic
// pre-flight token estimate above the Generate button. The over-
// budget banner explains *why* generation is blocked and what to do,
// per the brand voice rule "never blame the user."

export interface TokenBudgetDisplayProps {
  estimate: TokenBudgetEstimateDto | null | undefined;
  /**
   * When true, the over-budget state shows an "override" hint. The
   * parent flips this on when the current user holds the
   * Reports.GenerateLargeBudget permission.
   */
  canOverride?: boolean;
  className?: string;
}

function formatTokens(n: number): string {
  return n.toLocaleString(undefined);
}

export function TokenBudgetDisplay({
  estimate,
  canOverride = false,
  className,
}: TokenBudgetDisplayProps) {
  if (!estimate) {
    return (
      <div className={cn("rounded-md bg-white-warm px-space-3 py-space-2", className)}>
        <p className="font-body text-caption text-gray-mid">
          Token estimate will appear here once attachments are ready.
        </p>
      </div>
    );
  }

  const { totalTokens, thresholdLimit, exceedsThreshold } = estimate;

  if (exceedsThreshold) {
    return (
      <div
        role="alert"
        className={cn(
          "rounded-md border border-status-error bg-status-error/10",
          "px-space-3 py-space-3",
          className,
        )}
      >
        <div className="flex items-start gap-space-2">
          <AlertTriangle size={18} className="mt-[2px] text-status-error" aria-hidden />
          <div>
            <p className="font-body text-body font-semibold text-status-error">
              Over budget — request would consume ~{formatTokens(totalTokens)} tokens (limit{" "}
              {formatTokens(thresholdLimit)}).
            </p>
            <p className="font-body text-caption text-onyx mt-space-1">
              Reduce attachments or shorten the prompt.
              {canOverride
                ? " Admins can override the gate from this screen."
                : " Contact an admin if you need a one-time override."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-md border border-border-light bg-white-warm",
        "px-space-3 py-space-2",
        className,
      )}
    >
      <div className="flex items-center gap-space-2">
        <Check size={16} className="text-status-success" aria-hidden />
        <p className="font-body text-body text-onyx">
          Estimated input: <strong>~{formatTokens(totalTokens)}</strong> tokens
          <span className="text-gray-mid"> (limit {formatTokens(thresholdLimit)})</span>
        </p>
      </div>
    </div>
  );
}
